import type { QueryClient } from "@tanstack/react-query";

/** Minimal shape of a cached `["pipeline", jobId, ...]` page. */
interface CachedPipelinePage {
  candidates?:
    | {
        job_candidate_id?: string | null;
        ats_sync_state?: string;
        ats_sync_pending_action?: string | null;
      }[]
    | null;
}

/**
 * Optimistically flip synced pipeline rows to `queued` + pending action so the
 * badge shows Updating immediately after a client move/advance that may start
 * a non-create ATS push. Gated: only patches rows whose cached state is
 * exactly `synced` — never `not_synced` / `error` / `queued` (enqueue often
 * no-ops for those).
 */
export function patchSyncedCandidatesQueued(
  queryClient: QueryClient,
  jobId: string,
  jobCandidateIds: string[],
  pendingAction: string,
): void {
  if (jobCandidateIds.length === 0) return;
  const idSet = new Set(jobCandidateIds);

  queryClient.setQueriesData<CachedPipelinePage>(
    { queryKey: ["pipeline", jobId] },
    (data) => {
      if (!data?.candidates) return data;
      let changed = false;
      const candidates = data.candidates.map((c) => {
        if (
          !c.job_candidate_id ||
          !idSet.has(c.job_candidate_id) ||
          c.ats_sync_state !== "synced"
        ) {
          return c;
        }
        changed = true;
        return {
          ...c,
          ats_sync_state: "queued",
          ats_sync_pending_action: pendingAction,
        };
      });
      return changed ? { ...data, candidates } : data;
    },
  );
}
